import React, { useState } from 'react';
import { useWeb3 } from '@/hooks/useWeb3';
import { useTeamDistribution } from '@/hooks/useTeamDistribution';
import { usePublicClient, useWalletClient } from 'wagmi';
import { environment } from '@/config/environment';
import { TEAM_TOKEN_DISTRIBUTION_ABI } from '@/contracts/TeamTokenDistribution.abi';
import { ConfirmModal } from '@/components/ConfirmModal';
import { toast } from 'react-toastify';
import type { Address } from 'viem';

export const AdminPanel: React.FC = () => {
  const web3 = useWeb3();
  const teamDist = useTeamDistribution();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const contractAddress = environment.distributionAddress as Address;

  // Form states
  const [showSetTGEForm, setShowSetTGEForm] = useState(false);
  const [showSetEmploymentForm, setShowSetEmploymentForm] = useState(false);
  const [showCreateGrantForm, setShowCreateGrantForm] = useState(false);
  const [showCreateCustomGrantForm, setShowCreateCustomGrantForm] = useState(false);
  const [showTerminateForm, setShowTerminateForm] = useState(false);
  const [showViewGrantForm, setShowViewGrantForm] = useState(false);

  // TGE Form
  const [tgeDate, setTgeDate] = useState('');

  // Employment Form
  const [employmentAddress, setEmploymentAddress] = useState('');
  const [employmentDate, setEmploymentDate] = useState('');

  // Grant Form
  const [grantAddress, setGrantAddress] = useState('');
  const [grantAmount, setGrantAmount] = useState('');

  // Custom Grant Form
  const [customGrantAddress, setCustomGrantAddress] = useState('');
  const [customGrantAmount, setCustomGrantAmount] = useState('');
  const [customGrantDates, setCustomGrantDates] = useState<string[]>(['']);
  const [customGrantAmounts, setCustomGrantAmounts] = useState<string[]>(['']);

  // Terminate Form
  const [terminateAddress, setTerminateAddress] = useState('');

  // View Grant Form
  const [viewGrantAddress, setViewGrantAddress] = useState('');
  const [viewGrantId, setViewGrantId] = useState('');
  const [viewGrantIdOnly, setViewGrantIdOnly] = useState('');
  const [maxGrantId, setMaxGrantId] = useState<bigint | null>(null);
  const [grantDetails, setGrantDetails] = useState<{
    id: bigint;
    beneficiary?: string;
    totalAmount: bigint;
    grantDate: bigint;
    releasedAmount: bigint;
    useCustomSchedule: boolean;
    isActive: boolean;
    releasableAmount: bigint;
  } | null>(null);

  // Grants List View
  const [showGrantsList, setShowGrantsList] = useState(false);
  const [grantsList, setGrantsList] = useState<Array<{
    id: bigint;
    beneficiary: string;
    totalAmount: bigint;
    grantDate: bigint;
    releasedAmount: bigint;
    useCustomSchedule: boolean;
    isActive: boolean;
  }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalGrants, setTotalGrants] = useState(0);
  const grantsPerPage = 10;

  // Grant Management Modal
  const [selectedGrant, setSelectedGrant] = useState<any>(null);
  const [grantReleasableAmount, setGrantReleasableAmount] = useState<bigint>(BigInt(0));
  const [grantSchedule, setGrantSchedule] = useState<any[]>([]);
  const [showGrantDetailsModal, setShowGrantDetailsModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal states (only for confirm)
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const showTxErrorToast = (err: any, fallbackMsg = 'Transaction failed') => {
    let msg = err?.message || fallbackMsg;
    if (msg?.toLowerCase().includes('user rejected')) {
      msg = 'Transaction rejected by user.';
    } else if (msg?.toLowerCase().includes('cancel')) {
      msg = 'Transaction cancelled.';
    }
    toast.error(msg);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal({ ...confirmModal, isOpen: false });
  };

  const handleCopyContractAddress = async () => {
    try {
      await navigator.clipboard.writeText(contractAddress);
      toast.success('Contract address copied!');
    } catch {
      toast.error('Failed to copy address');
    }
  };

  const handleSetTGE = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tgeDate) {
      toast.warning('Please select a TGE date');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const timestamp = Math.floor(new Date(tgeDate).getTime() / 1000);

      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'setTGEDate',
        args: [BigInt(timestamp)],
      });

      await publicClient?.waitForTransactionReceipt({ hash });

      toast.success(`TGE date has been set to ${new Date(timestamp * 1000).toLocaleString()}`);
      setTgeDate('');
      setShowSetTGEForm(false);
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to set TGE date');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetEmployment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employmentAddress || !employmentDate) {
      toast.warning('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const timestamp = Math.floor(new Date(employmentDate).getTime() / 1000);

      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'setEmploymentDate',
        args: [employmentAddress as Address, BigInt(timestamp)],
      });

      await publicClient?.waitForTransactionReceipt({ hash });

      toast.success(`Employment date set for ${employmentAddress}`);
      setEmploymentAddress('');
      setEmploymentDate('');
      setShowSetEmploymentForm(false);
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to set employment date');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grantAddress || !grantAmount) {
      toast.warning('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      // Convert amount to BigInt with 18 decimals
      // Use string manipulation to avoid floating point precision issues
      const amountStr = grantAmount.toString();
      const [whole, fraction = ''] = amountStr.split('.');
      const paddedFraction = fraction.padEnd(18, '0').slice(0, 18);
      const amount = BigInt(whole + paddedFraction);

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'createVestingGrant',
        args: [grantAddress as Address, amount],
      });

      const receipt = await publicClient?.waitForTransactionReceipt({ hash });

      toast.success(`Vesting grant created for ${grantAddress}`);
      setGrantAddress('');
      setGrantAmount('');
      setShowCreateGrantForm(false);
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to create grant');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCustomGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customGrantAddress || !customGrantAmount) {
      toast.warning('Please fill in all required fields');
      return;
    }

    // Validate custom dates and amounts
    const validDates = customGrantDates.filter((d) => d.trim() !== '');
    const validAmounts = customGrantAmounts.filter((a) => a.trim() !== '');

    if (validDates.length === 0 || validAmounts.length === 0) {
      toast.warning('Please add at least one vesting date and amount');
      return;
    }

    if (validDates.length !== validAmounts.length) {
      toast.warning('Number of dates must match number of amounts');
      return;
    }

    // Check that amounts sum to 100%
    const totalPercentage = validAmounts.reduce((sum, a) => sum + parseFloat(a || '0'), 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.warning(`Amounts must sum to 100% (currently ${totalPercentage.toFixed(2)}%)`);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      // Convert amount to BigInt with 18 decimals
      const amountStr = customGrantAmount.toString();
      const [whole, fraction = ''] = amountStr.split('.');
      const paddedFraction = fraction.padEnd(18, '0').slice(0, 18);
      const amount = BigInt(whole + paddedFraction);

      // Convert dates to timestamps
      const timestamps = validDates.map((d) => BigInt(Math.floor(new Date(d).getTime() / 1000)));

      // Convert percentages to basis points (multiply by 100)
      const basisPoints = validAmounts.map((a) => BigInt(Math.floor(parseFloat(a) * 100)));

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'createCustomVestingGrant',
        args: [customGrantAddress as Address, amount, timestamps, basisPoints],
      });

      await publicClient?.waitForTransactionReceipt({ hash });

      toast.success(`Custom vesting grant created for ${customGrantAddress}`);
      setCustomGrantAddress('');
      setCustomGrantAmount('');
      setCustomGrantDates(['']);
      setCustomGrantAmounts(['']);
      setShowCreateCustomGrantForm(false);
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to create custom grant');
    } finally {
      setIsLoading(false);
    }
  };

  const addCustomScheduleLine = () => {
    setCustomGrantDates([...customGrantDates, '']);
    setCustomGrantAmounts([...customGrantAmounts, '']);
  };

  const removeCustomScheduleLine = (index: number) => {
    setCustomGrantDates(customGrantDates.filter((_, i) => i !== index));
    setCustomGrantAmounts(customGrantAmounts.filter((_, i) => i !== index));
  };

  const updateCustomDate = (index: number, value: string) => {
    const newDates = [...customGrantDates];
    newDates[index] = value;
    setCustomGrantDates(newDates);
  };

  const updateCustomAmount = (index: number, value: string) => {
    const newAmounts = [...customGrantAmounts];
    newAmounts[index] = value;
    setCustomGrantAmounts(newAmounts);
  };

  const handleTerminateEmployment = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const hash = await walletClient.writeContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'terminateEmployment',
        args: [terminateAddress as Address],
      });

      await publicClient?.waitForTransactionReceipt({ hash });

      toast.success(`Employment terminated for ${terminateAddress}. Vested tokens will continue to unlock.`);
      setTerminateAddress('');
      setShowTerminateForm(false);
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to terminate employment');
    } finally {
      setIsLoading(false);
    }
  };

  const promptTerminateEmployment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!terminateAddress) {
      toast.warning('Please enter a beneficiary address');
      return;
    }
    showConfirm(
      'Terminate Employment',
      `Terminate employment for:\n${terminateAddress}\n\nVested tokens will continue to unlock according to the TGE schedule.`,
      handleTerminateEmployment
    );
  };

  const handleFetchMaxGrantId = async () => {
    try {
      setIsLoading(true);
      const maxId = await teamDist.getMaxGrantId();
      setMaxGrantId(maxId);
      toast.success(`Max Grant ID: ${maxId.toString()}`);
    } catch (err: any) {
      toast.error('Failed to fetch max grant ID');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewGrantAddress || !viewGrantId) {
      toast.warning('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setGrantDetails(null);

      if (!publicClient) {
        throw new Error('Public client not available');
      }

      // Fetch grant details
      const grant = await publicClient.readContract({
          address: contractAddress,
          abi: TEAM_TOKEN_DISTRIBUTION_ABI,
          functionName: 'getVestingGrant',
          args: [BigInt(viewGrantId)],
        }) as { id: bigint, beneficiaryAddress: string, totalAmount: bigint, grantDate: bigint, firstVestingDate: bigint, releasedAmount: bigint, customDates: bigint[], customAmounts: bigint[], useCustomSchedule: boolean, isActive: boolean };

      // Fetch releasable amount for this grant
      const releasableAmount = await publicClient.readContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'releasableAmountForGrant',
        args: [viewGrantAddress as Address, BigInt(viewGrantId)],
      }) as bigint;

      setGrantDetails({
        id: grant.id,
        beneficiary: grant.beneficiaryAddress,
        totalAmount: grant.totalAmount,
        grantDate: grant.grantDate,
        releasedAmount: grant.releasedAmount,
        useCustomSchedule: grant.useCustomSchedule,
        isActive: grant.isActive,
        releasableAmount,
      });

      toast.success(`Successfully fetched grant #${id.toString()}`);
    } catch (err: any) {
      toast.error('Failed to fetch grant details. The grant may not exist for this address.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewGrantById = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewGrantIdOnly) {
      toast.warning('Please enter a grant ID');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setGrantDetails(null);

      const grantId = BigInt(viewGrantIdOnly);
      const grant = await teamDist.getGrantById(grantId);

      // Note: releasableAmount requires beneficiary address, so we fetch it separately
      const releasableAmount = await publicClient?.readContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'releasableAmountForGrant',
        args: [grant.beneficiary, grantId],
      }) as bigint;

      setGrantDetails({
        id: grant.id,
        beneficiary: grant.beneficiary,
        totalAmount: grant.totalAmount,
        grantDate: grant.grantDate,
        releasedAmount: grant.releasedAmount,
        useCustomSchedule: grant.useCustomSchedule,
        isActive: grant.isActive,
        releasableAmount,
      });

      toast.success(`Successfully fetched grant #${grant.id.toString()}`);
    } catch (err: any) {
      toast.error('Failed to fetch grant details. The grant ID may not exist.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGrantsList = async (page: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      // Get max grant ID first
      const maxId = await teamDist.getMaxGrantId();
      setTotalGrants(Number(maxId));

      if (maxId === BigInt(0)) {
        toast.info('No grants have been created yet');
        setGrantsList([]);
        return;
      }

      // Calculate which grants to load for this page
      const startId = Number(maxId) - (page - 1) * grantsPerPage;
      const endId = Math.max(1, startId - grantsPerPage + 1);

      // Load grants for this page
      const grants = [];
      for (let i = startId; i >= endId; i--) {
        try {
          const grant = await teamDist.getGrantById(BigInt(i));
          grants.push({
            id: grant.id,
            beneficiary: grant.beneficiary,
            totalAmount: grant.totalAmount,
            grantDate: grant.grantDate,
            releasedAmount: grant.releasedAmount,
            useCustomSchedule: grant.useCustomSchedule,
            isActive: grant.isActive,
          });
        } catch (err) {
          console.error(`Error loading grant ${i}:`, err);
        }
      }

      setGrantsList(grants);
      setCurrentPage(page);
      toast.success(`Loaded ${grants.length} grants`);
    } catch (err: any) {
      toast.error('Failed to load grants list');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    loadGrantsList(newPage);
  };

  const handleViewGrantDetails = async (grant: any) => {
    try {
      setIsLoading(true);
      setSelectedGrant(grant);

      // Fetch releasable amount for this grant
      const releasableAmount = await publicClient?.readContract({
        address: contractAddress,
        abi: TEAM_TOKEN_DISTRIBUTION_ABI,
        functionName: 'releasableAmountForGrant',
        args: [grant.beneficiary as Address, grant.id],
      }) as bigint;

      setGrantReleasableAmount(releasableAmount);

      // Fetch custom schedule if applicable
      if (grant.useCustomSchedule) {
        const schedule = await teamDist.getCustomScheduleById(grant.id);
        if (schedule) {
          const scheduleItems = schedule.dates.map((date, idx) => ({
            date: new Date(Number(date) * 1000),
            percentage: Number(schedule.amounts[idx]) / 100,
            amount: (grant.totalAmount * schedule.amounts[idx]) / BigInt(10000),
            unlocked: Math.floor(Date.now() / 1000) >= Number(date),
          }));
          setGrantSchedule(scheduleItems);
        }
      } else {
        setGrantSchedule([]);
      }

      setShowGrantDetailsModal(true);
    } catch (err: any) {
      toast.error('Failed to fetch grant details');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReleaseFromGrant = async () => {
    if (!selectedGrant) return;

    try {
      setIsLoading(true);
      await teamDist.releaseFromGrant(selectedGrant.id);
      toast.success(`Successfully released tokens from grant #${selectedGrant.id.toString()}`);
      setShowGrantDetailsModal(false);
      // Refresh the grants list
      await loadGrantsList(currentPage);
    } catch (err: any) {
      const errorMessage = err?.message || err?.toString() || '';
      if (errorMessage.includes('Insufficient token balance in contract')) {
        toast.error('Contract does not have enough tokens. Please add more tokens to the contract.');
      } else {
        toast.error('Failed to release tokens');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTerminateEmployee = async (beneficiaryAddress: string) => {
    if (!beneficiaryAddress) return;

    showConfirm(
      'Terminate Employment',
      `Are you sure you want to terminate employment for ${beneficiaryAddress}? This will stop future vesting but already vested tokens will continue to unlock according to the TGE schedule.`,
      async () => {
        try {
          setIsLoading(true);

          if (!walletClient) {
            toast.error('Wallet not connected');
            return;
          }

          const hash = await walletClient.writeContract({
            address: contractAddress,
            abi: TEAM_TOKEN_DISTRIBUTION_ABI,
            functionName: 'terminateEmployment',
            args: [beneficiaryAddress as Address],
          });

          await publicClient?.waitForTransactionReceipt({ hash });
          toast.success('Employment terminated successfully');
          setShowGrantDetailsModal(false);
          // Refresh the grants list
          await loadGrantsList(currentPage);
        } catch (err: any) {
          toast.error(err.message || 'Failed to terminate employment');
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  return (
    <div className="space-y-6">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          confirmModal.onConfirm();
          closeConfirm();
        }}
        onCancel={closeConfirm}
      />

      {/* Grant Details Management Modal */}
      {showGrantDetailsModal && selectedGrant && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-primary/20 rounded-2xl p-8 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gradient-primary">Grant #{selectedGrant.id.toString()} Details</h3>
              <button
                onClick={() => setShowGrantDetailsModal(false)}
                className="text-gray-400 hover:text-gray-200 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Beneficiary</p>
                  <div className="flex items-center gap-2">
                    <p className="text-gray-200 font-mono text-sm break-all">{selectedGrant.beneficiary}</p>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(selectedGrant.beneficiary);
                          toast.success('Address copied!');
                        } catch {
                          toast.error('Failed to copy');
                        }
                      }}
                      className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-all flex-shrink-0"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Grant Date</p>
                  <p className="text-gray-200 font-semibold">
                    {new Date(Number(selectedGrant.grantDate) * 1000).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Total Amount</p>
                  <p className="text-gray-200 font-semibold">{web3.formatTokenAmount(selectedGrant.totalAmount)}</p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Released Amount</p>
                  <p className="text-gray-200 font-semibold">{web3.formatTokenAmount(selectedGrant.releasedAmount)}</p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Releasable Amount</p>
                  <p className="text-green-400 font-semibold text-lg">{web3.formatTokenAmount(grantReleasableAmount)}</p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    {selectedGrant.isActive ? (
                      <span className="text-green-400 font-semibold">✓ Active</span>
                    ) : (
                      <span className="text-red-400 font-semibold">✗ Inactive</span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs ${selectedGrant.useCustomSchedule ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                      {selectedGrant.useCustomSchedule ? 'Custom' : 'Default'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Vesting Schedule for Custom Grants */}
              {selectedGrant.useCustomSchedule && grantSchedule.length > 0 && (
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-200 mb-3">Custom Vesting Schedule</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {grantSchedule.map((item, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          item.unlocked ? 'bg-green-500/10 border border-green-500/20' : 'bg-gray-800/50 border border-gray-700/20'
                        }`}
                      >
                        <div>
                          <p className="text-gray-300 font-medium">{item.date.toLocaleDateString()}</p>
                          <p className="text-gray-500 text-sm">{item.percentage.toFixed(2)}%</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-200 font-semibold">{web3.formatTokenAmount(item.amount)}</p>
                          <p className={`text-xs ${item.unlocked ? 'text-green-400' : 'text-gray-500'}`}>
                            {item.unlocked ? '✓ Unlocked' : '🔒 Locked'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-primary/10">
                <button
                  onClick={handleReleaseFromGrant}
                  disabled={isLoading || grantReleasableAmount === BigInt(0)}
                  className="flex-1 px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Processing...' : `Release ${web3.formatTokenAmount(grantReleasableAmount)} Tokens`}
                </button>
                <button
                  onClick={() => handleTerminateEmployee(selectedGrant.beneficiary)}
                  disabled={isLoading || !selectedGrant.isActive}
                  className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Terminate Employee
                </button>
                <button
                  onClick={() => setShowGrantDetailsModal(false)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(error || teamDist.error) && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 backdrop-blur-sm">
          <div className="flex items-start justify-between">
            <p className="text-red-200 text-sm">{error || teamDist.error}</p>
            <button onClick={() => { setError(null); teamDist.setError(null); }} className="text-red-400 hover:text-red-300">✕</button>
          </div>
        </div>
      )}

      {/* Contract Info */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <h2 className="text-2xl font-bold text-gradient-primary mb-4">Contract Information</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Contract Address:</span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-gray-300">{web3.formatAddress(contractAddress)}</span>
              <button
                type="button"
                onClick={handleCopyContractAddress}
                className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-all border border-gray-600"
                title="Copy to clipboard"
              >
                Copy
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">TGE Date:</span>
            <span className="text-gray-300">
              {teamDist.tgeDate && teamDist.tgeDate > BigInt(0)
                ? new Date(Number(teamDist.tgeDate) * 1000).toLocaleString()
                : 'Not set'}
            </span>
          </div>
        </div>
      </div>

      {/* Set TGE Date */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gradient-primary">Set TGE Date</h2>
          <button
            onClick={() => setShowSetTGEForm(!showSetTGEForm)}
            className="px-4 py-2 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200"
          >
            {showSetTGEForm ? 'Cancel' : 'Set TGE'}
          </button>
        </div>

        {showSetTGEForm && (
          <form onSubmit={handleSetTGE} className="space-y-4 mt-4">
            <div>
              <label className="block text-gray-300 mb-2">TGE Date & Time</label>
              <input
                type="datetime-local"
                value={tgeDate}
                onChange={(e) => setTgeDate(e.target.value)}
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This sets the unlock schedule start date for all grants</p>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Setting...' : 'Set TGE Date'}
            </button>
          </form>
        )}
      </div>

      {/* Set Employment Date */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gradient-primary">Set Employment Date</h2>
          <button
            onClick={() => setShowSetEmploymentForm(!showSetEmploymentForm)}
            className="px-4 py-2 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200"
          >
            {showSetEmploymentForm ? 'Cancel' : 'Set Employment'}
          </button>
        </div>

        {showSetEmploymentForm && (
          <form onSubmit={handleSetEmployment} className="space-y-4 mt-4">
            <div>
              <label className="block text-gray-300 mb-2">Beneficiary Address</label>
              <input
                type="text"
                value={employmentAddress}
                onChange={(e) => setEmploymentAddress(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Employment Start Date & Time</label>
              <input
                type="datetime-local"
                value={employmentDate}
                onChange={(e) => setEmploymentDate(e.target.value)}
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This date determines vesting start for grants</p>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Setting...' : 'Set Employment Date'}
            </button>
          </form>
        )}
      </div>

      {/* Create Vesting Grant */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gradient-primary">Create Vesting Grant</h2>
          <button
            onClick={() => setShowCreateGrantForm(!showCreateGrantForm)}
            className="px-4 py-2 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200"
          >
            {showCreateGrantForm ? 'Cancel' : 'New Grant'}
          </button>
        </div>

        {showCreateGrantForm && (
          <form onSubmit={handleCreateGrant} className="space-y-4 mt-4">
            <div>
              <label className="block text-gray-300 mb-2">Beneficiary Address</label>
              <input
                type="text"
                value={grantAddress}
                onChange={(e) => setGrantAddress(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Employment date must be set first</p>
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Total Amount (tokens)</label>
              <input
                type="number"
                step="0.000001"
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
                placeholder="1000000"
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Default vesting: 1-year cliff (25%), then 36 months (2.08%/month)</p>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Grant'}
            </button>
          </form>
        )}
      </div>

      {/* Create Custom Vesting Grant */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gradient-primary">Create Custom Vesting Grant</h2>
          <button
            onClick={() => setShowCreateCustomGrantForm(!showCreateCustomGrantForm)}
            className="px-4 py-2 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200"
          >
            {showCreateCustomGrantForm ? 'Cancel' : 'New Custom Grant'}
          </button>
        </div>

        {showCreateCustomGrantForm && (
          <form onSubmit={handleCreateCustomGrant} className="space-y-4 mt-4">
            <div>
              <label className="block text-gray-300 mb-2">Beneficiary Address</label>
              <input
                type="text"
                value={customGrantAddress}
                onChange={(e) => setCustomGrantAddress(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Total Amount (tokens)</label>
              <input
                type="number"
                step="0.000001"
                value={customGrantAmount}
                onChange={(e) => setCustomGrantAmount(e.target.value)}
                placeholder="1000000"
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-gray-300">Custom Vesting Schedule</label>
                <button
                  type="button"
                  onClick={addCustomScheduleLine}
                  className="px-3 py-1 bg-primary/20 hover:bg-primary/30 text-primary text-sm font-semibold rounded transition-all duration-200"
                >
                  + Add Date
                </button>
              </div>

              {customGrantDates.map((date, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <div className="flex-1">
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => updateCustomDate(index, e.target.value)}
                      className="w-full px-3 py-2 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 text-sm focus:outline-none focus:border-primary/50"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={customGrantAmounts[index]}
                        onChange={(e) => updateCustomAmount(index, e.target.value)}
                        placeholder="25"
                        className="w-full px-3 py-2 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 text-sm focus:outline-none focus:border-primary/50"
                        required
                      />
                      <span className="absolute right-3 top-2 text-gray-500 text-sm">%</span>
                    </div>
                  </div>
                  {customGrantDates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCustomScheduleLine(index)}
                      className="px-2 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-all duration-200"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}

              <p className="text-xs text-gray-500">
                Total: {customGrantAmounts.reduce((sum, a) => sum + (parseFloat(a) || 0), 0).toFixed(2)}% (must equal 100%)
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Custom Grant'}
            </button>
          </form>
        )}
      </div>

      {/* Terminate Employment */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gradient-primary">Terminate Employment</h2>
          <button
            onClick={() => setShowTerminateForm(!showTerminateForm)}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all duration-200"
          >
            {showTerminateForm ? 'Cancel' : 'Terminate'}
          </button>
        </div>

        {showTerminateForm && (
          <form onSubmit={promptTerminateEmployment} className="space-y-4 mt-4">
            <div>
              <label className="block text-gray-300 mb-2">Beneficiary Address</label>
              <input
                type="text"
                value={terminateAddress}
                onChange={(e) => setTerminateAddress(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                required
              />
              <p className="text-xs text-orange-300 mt-1">⚠️ Vesting stops immediately. Already vested tokens continue to unlock per TGE schedule.</p>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Terminating...' : 'Terminate Employment'}
            </button>
          </form>
        )}
      </div>

      {/* Grants List */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gradient-primary">All Grants</h2>
          <button
            onClick={() => {
              setShowGrantsList(!showGrantsList);
              if (!showGrantsList) {
                loadGrantsList(1);
              }
            }}
            disabled={isLoading}
            className="px-4 py-2 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : (showGrantsList ? 'Hide List' : 'Show All Grants')}
          </button>
        </div>

        {showGrantsList && (
          <div className="space-y-4 mt-4">
            {grantsList.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No grants found</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">ID</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Beneficiary</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Total Amount</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Released</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Grant Date</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Type</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Status</th>
                        <th className="text-left py-3 px-4 text-gray-300 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grantsList.map((grant) => (
                        <tr key={grant.id.toString()} className="border-b border-primary/10 hover:bg-dark-lighter/30 transition-colors">
                          <td className="py-3 px-4 text-gray-200 font-mono">#{grant.id.toString()}</td>
                          <td className="py-3 px-4 text-gray-200 font-mono text-sm">
                            {web3.formatAddress(grant.beneficiary)}
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(grant.beneficiary);
                                  toast.success('Address copied!');
                                } catch {
                                  toast.error('Failed to copy');
                                }
                              }}
                              className="ml-2 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-all"
                              title="Copy address"
                            >
                              📋
                            </button>
                          </td>
                          <td className="py-3 px-4 text-gray-200">{web3.formatTokenAmount(grant.totalAmount)}</td>
                          <td className="py-3 px-4 text-gray-200">{web3.formatTokenAmount(grant.releasedAmount)}</td>
                          <td className="py-3 px-4 text-gray-200 text-sm">
                            {new Date(Number(grant.grantDate) * 1000).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${grant.useCustomSchedule ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                              {grant.useCustomSchedule ? 'Custom' : 'Default'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {grant.isActive ? (
                              <span className="text-green-400">✓ Active</span>
                            ) : (
                              <span className="text-red-400">✗ Inactive</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleViewGrantDetails(grant)}
                              disabled={isLoading}
                              className="px-3 py-1.5 bg-primary hover:bg-primary-light text-dark font-semibold rounded-lg text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              title="View details and manage grant"
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-4 border-t border-primary/10">
                  <p className="text-gray-400 text-sm">
                    Showing {((currentPage - 1) * grantsPerPage) + 1} - {Math.min(currentPage * grantsPerPage, totalGrants)} of {totalGrants} grants
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || isLoading}
                      className="px-4 py-2 bg-dark-lighter hover:bg-dark text-gray-300 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <span className="px-4 py-2 bg-dark-lighter text-gray-300 rounded-lg">
                      Page {currentPage} of {Math.ceil(totalGrants / grantsPerPage)}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= Math.ceil(totalGrants / grantsPerPage) || isLoading}
                      className="px-4 py-2 bg-dark-lighter hover:bg-dark text-gray-300 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* View Grant Details */}
      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gradient-primary">View Grant Details</h2>
            {maxGrantId !== null && (
              <p className="text-sm text-gray-400 mt-1">Max Grant ID: {maxGrantId.toString()}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleFetchMaxGrantId}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Get Max ID'}
            </button>
            <button
              onClick={() => {
                setShowViewGrantForm(!showViewGrantForm);
                if (!showViewGrantForm) {
                  setGrantDetails(null);
                }
              }}
              className="px-4 py-2 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200"
            >
              {showViewGrantForm ? 'Cancel' : 'View Grant'}
            </button>
          </div>
        </div>

        {showViewGrantForm && (
          <div className="space-y-6 mt-4">
            {/* View by Grant ID Only */}
            <div className="bg-dark-lighter/50 border border-primary/10 rounded-xl p-4">
              <h4 className="text-lg font-semibold text-gray-200 mb-3">View by Grant ID</h4>
              <form onSubmit={handleViewGrantById} className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Grant ID</label>
                  <input
                    type="number"
                    min="1"
                    value={viewGrantIdOnly}
                    onChange={(e) => setViewGrantIdOnly(e.target.value)}
                    placeholder="Enter grant ID (1, 2, 3...)"
                    className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Query any grant directly by its ID without needing to know the beneficiary address</p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Loading...' : 'View Grant by ID'}
                </button>
              </form>
            </div>

            {/* View by Beneficiary + Grant ID (legacy) */}
            <div className="bg-dark-lighter/50 border border-primary/10 rounded-xl p-4">
              <h4 className="text-lg font-semibold text-gray-200 mb-3">View by Beneficiary Address</h4>
              <form onSubmit={handleViewGrant} className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Beneficiary Address</label>
                  <input
                    type="text"
                    value={viewGrantAddress}
                    onChange={(e) => setViewGrantAddress(e.target.value)}
                    placeholder="0x..."
                    className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Grant ID</label>
                  <input
                    type="number"
                    min="1"
                    value={viewGrantId}
                    onChange={(e) => setViewGrantId(e.target.value)}
                    placeholder="1"
                    className="w-full px-4 py-3 bg-dark-lighter border border-primary/20 rounded-lg text-gray-200 focus:outline-none focus:border-primary/50"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Alternative method: specify both beneficiary address and grant ID</p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Loading...' : 'View Grant Details'}
                </button>
              </form>
            </div>
          </div>
        )}

        {grantDetails && (
          <div className="mt-6 space-y-4 bg-dark-lighter border border-primary/10 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gradient-primary mb-4">Grant #{grantDetails.id.toString()}</h3>

            {grantDetails.beneficiary && (
              <div className="mb-4 pb-4 border-b border-primary/10">
                <p className="text-xs text-gray-500">Beneficiary Address</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm font-mono text-gray-200">{grantDetails.beneficiary}</p>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(grantDetails.beneficiary!);
                        toast.success('Address copied!');
                      } catch {
                        toast.error('Failed to copy');
                      }
                    }}
                    className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-all border border-gray-600"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="text-lg font-semibold text-gray-200">
                    {grantDetails.isActive ? (
                      <span className="text-green-400">✓ Active</span>
                    ) : (
                      <span className="text-red-400">✗ Inactive</span>
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="text-lg font-semibold text-gray-200">
                    {web3.formatTokenAmount(grantDetails.totalAmount)} tokens
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Released Amount</p>
                  <p className="text-lg font-semibold text-gray-200">
                    {web3.formatTokenAmount(grantDetails.releasedAmount)} tokens
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Releasable Now</p>
                  <p className="text-lg font-semibold text-primary">
                    {web3.formatTokenAmount(grantDetails.releasableAmount)} tokens
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Grant Date</p>
                  <p className="text-lg font-semibold text-gray-200">
                    {new Date(Number(grantDetails.grantDate) * 1000).toLocaleString()}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Schedule Type</p>
                  <p className="text-lg font-semibold text-gray-200">
                    {grantDetails.useCustomSchedule ? (
                      <span className="text-blue-400">Custom</span>
                    ) : (
                      <span className="text-gray-300">Default (1yr cliff + 3yr vesting)</span>
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Remaining Amount</p>
                  <p className="text-lg font-semibold text-gray-200">
                    {web3.formatTokenAmount(grantDetails.totalAmount - grantDetails.releasedAmount)} tokens
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Progress</p>
                  <div className="mt-2">
                    <div className="flex mb-1 items-center justify-between">
                      <span className="text-xs font-semibold text-primary">
                        {grantDetails.totalAmount > BigInt(0)
                          ? ((Number(grantDetails.releasedAmount) / Number(grantDetails.totalAmount)) * 100).toFixed(2)
                          : '0'}%
                      </span>
                    </div>
                    <div className="overflow-hidden h-2 text-xs flex rounded-full bg-dark">
                      <div
                        style={{
                          width: `${grantDetails.totalAmount > BigInt(0)
                            ? ((Number(grantDetails.releasedAmount) / Number(grantDetails.totalAmount)) * 100)
                            : 0}%`
                        }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-dark justify-center bg-primary transition-all duration-500 rounded-full"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
